import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import { isShopRef, type ShopCardBlockConfig } from '@front-platform/content-builder';
import { shopCardBlockResolveConfig } from '@front-platform/content-builder';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { Component, DestroyRef, inject, input, OnInit, computed, signal } from '@angular/core';
import { BehaviorSubject, EMPTY, Observable, combineLatest } from 'rxjs';

import { EntityKeys } from '@config/keys/entity.keys';
import { SettingsParametersState } from '@store/settings';
import { EntityStoreSelector } from '@common/selectors/entity.selector';

import {
  IContentBuilderFieldColor,
  IContentBuilderFieldImage,
} from '@core/models/content-builder-fields.model';
import { DoodEntityType, DoodEntityModel, DoodEntityStatus } from '@core/models/entity.model';

import { ShopsService } from '@core/services/shops/shops.service';
import { EntitiesService } from '@core/services/entities/entities.service';
import { EntityStatusService } from '@core/services/entity-status/entity-status.service';
import { ShopSearchParametersService } from '@core/services/shop-search-parameters/shop-search-parameters.service';
import { TemplateLayoutKeys } from '@config/keys/template-layout.keys';
import {
  EntityCategoryBadgeSource,
  resolveEntityCategoryBadge,
} from '@shared/utils/entity-category-badge/entity-category-badge.utils';
import {
  asDisplayText,
  resolveEntityDisplayName,
} from '@shared/utils/entity-display-name/entity-display-name.utils';

export interface ShopCardBlockRuntimeConfig {
  'display_opened_shop'?: boolean;
  'display_closed_shop'?: boolean;
  'display_entity_distance'?: boolean;
  'display_entity_pricing'?: boolean;
  'display_entity_address'?: boolean;
  'entity_category_badge_source'?: EntityCategoryBadgeSource;
  'entity_name_color'?: IContentBuilderFieldColor;
  'kiosk_card_aspect_ratio'?: string;
  'template_layout'?: TemplateLayoutKeys;
  'entity_image_type'?: 'cover' | 'overview';
}

export type ShopCardBlockConfigInput = Partial<
  Omit<ShopCardBlockConfig, keyof ShopCardBlockRuntimeConfig> & ShopCardBlockRuntimeConfig
>;
export type ShopCardBlockResolvedConfig = Omit<
  ShopCardBlockConfig,
  keyof ShopCardBlockRuntimeConfig
> &
  Partial<ShopCardBlockRuntimeConfig>;
export interface ShopCardBlockContract extends ConfigComponentContract<
  ShopCardBlockConfigInput,
  ShopCardBlockResolvedConfig
> {}

@Component({
  selector: 'app-shop-card-block',
  templateUrl: './shop-card-block.component.html',
  standalone: false,
})
export class ShopCardBlockComponent implements OnInit, ShopCardBlockContract {
  readonly config = input<ShopCardBlockConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<ShopCardBlockConfig, ShopCardBlockRuntimeConfig>(
    shopCardBlockResolveConfig,
    () => ({ config: this.config() }),
  );
  readonly shop = computed((): DoodEntityModel[] => {
    const value = this.resolvedConfig()['shop'];
    if (isShopRef(value)) {
      return [
        {
          id: value.shopId,
          name: value.shopName ?? value.shopSlug
        } as DoodEntityModel
      ];
    }
    // Keep CB1 shop_picker payloads ({ id, name, position }[]) working.
    return Array.isArray(value) ? (value as DoodEntityModel[]) : [];
  });
  readonly displayOpenedShop = computed(
    () => this.resolvedConfig()['display_opened_shop'] as unknown as boolean,
  );
  readonly displayClosedShop = computed(
    () => this.resolvedConfig()['display_closed_shop'] as unknown as boolean,
  );
  readonly displayEntityName = computed(
    () => this.resolvedConfig()['display_entity_name'] as unknown as boolean,
  );
  readonly displayEntityImage = computed(
    () => this.resolvedConfig()['display_entity_image'] as unknown as boolean,
  );
  readonly displayEntityDistance = computed(() => {
    const value = this.resolvedConfig()['display_entity_distance'];
    return value === undefined ? true : (value as unknown as boolean);
  });
  readonly displayEntityPricing = computed(() => {
    const value = this.resolvedConfig()['display_entity_pricing'];
    return value === undefined ? true : (value as unknown as boolean);
  });
  readonly displayEntityAddress = computed(
    () => this.resolvedConfig()['display_entity_address'] as unknown as boolean,
  );
  readonly displayEntityCategory = computed(
    () => this.resolvedConfig()['display_entity_category'] as unknown as boolean,
  );
  readonly entityNameSize = computed(
    () => this.resolvedConfig()['entity_name_size'] as unknown as string,
  );
  readonly entityName = computed(() => this.resolvedConfig()['entity_name'] as unknown);
  readonly entityCategory = computed(
    () => this.resolvedConfig()['entity_category'] as unknown as string,
  );
  readonly image = computed(
    () => this.resolvedConfig()['image'] as unknown as IContentBuilderFieldImage,
  );
  readonly preserveImageDimensions = computed(
    () => this.resolvedConfig()['preserve_image_dimensions'] as unknown as boolean,
  );
  readonly imageBackgroundColor = computed(
    () => this.resolvedConfig()['image_background_color'] as unknown as IContentBuilderFieldColor,
  );
  readonly kioskCardAspectRatio = computed(
    () => this.resolvedConfig()['kiosk_card_aspect_ratio'] as unknown as string,
  );
  readonly templateLayout = computed(
    () => this.resolvedConfig()['template_layout'] as unknown as TemplateLayoutKeys,
  );
  readonly entityImageType = computed(
    () => this.resolvedConfig()['entity_image_type'] as unknown as 'cover' | 'overview',
  );
  readonly entityCategoryBadgeSource = computed(
    () =>
      (this.resolvedConfig()['entity_category_badge_source'] as EntityCategoryBadgeSource) ??
      'subcategory',
  );
  readonly entityNameColor = computed(
    () => this.resolvedConfig()['entity_name_color'] as unknown as IContentBuilderFieldColor,
  );

  protected readonly entity = signal<DoodEntityModel | undefined>(undefined);
  protected shopImage?: string;
  protected status?: DoodEntityStatus;

  readonly title = computed(() =>
    resolveEntityDisplayName({
      override: this.entityName(),
      entityName: this.entity()?.[EntityKeys.Name],
      fallbackName: this.shop()?.[0]?.[EntityKeys.Name],
    }),
  );
  readonly categoryLabel = computed(
    () =>
      asDisplayText(this.entityCategory()) ||
      this.entity()?.[EntityKeys.Type]?.[EntityKeys.Name] ||
      '',
  );

  readonly entityKeys = EntityKeys;
  isVisible$ = new BehaviorSubject<boolean>(false);
  private readonly shopId$ = toObservable(computed(() => this.shop()?.[0]?.id));

  componentFactoryIsBlockVisible$(): Observable<boolean> {
    return this.isVisible$;
  }

  private readonly entitiesService = inject(EntitiesService);
  private readonly shopsService = inject(ShopsService);
  private readonly entitySelector = inject(EntityStoreSelector);
  private readonly entityStatusService = inject(EntityStatusService);
  private readonly shopSearchParametersService = inject(ShopSearchParametersService);
  private readonly destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.shopId$
      .pipe(
        map(shopId => shopId || undefined),
        distinctUntilChanged(),
        switchMap(shopId => {
          if (!shopId) {
            this.entity.set(undefined);
            this.shopImage = undefined;
            this.updateIsVisible();
            return EMPTY;
          }

          return combineLatest([
            this.entitySelector.selectEntity(shopId),
            this.shopSearchParametersService.getParameters$(),
          ]);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(([entity, parameters]) => {
        this.loadEntity(entity, parameters);
        this.updateIsVisible();
      });
  }

  protected getClosedLabel(entity: DoodEntityModel): string | null {
    if (entity[EntityKeys.EntityType] === DoodEntityType.Marketplace) {
      return null;
    }
    const distributionMode = this.shopSearchParametersService.getDistributionMode();
    return this.shopsService.getClosedMessage(entity, null, distributionMode);
  }

  protected isShopOpened(entity: DoodEntityModel): boolean {
    const label = this.getClosedLabel(entity);
    return !label;
  }

  protected getEntityUrl(entity: DoodEntityModel | undefined): string {
    return entity ? this.entitiesService.getUrl(entity) : '';
  }

  protected getEntityAddress(entity: DoodEntityModel | undefined): string {
    return entity ? this.entitiesService.getFullAddress(entity) : '';
  }

  protected getCategoryBadge(entity: DoodEntityModel | undefined): string {
    return resolveEntityCategoryBadge({
      override: asDisplayText(this.entityCategory()),
      category: entity?.[EntityKeys.Type]?.[EntityKeys.Name] ?? '',
      shortDescription: entity?.[EntityKeys.ShortDescription] ?? '',
      badgeSource: this.entityCategoryBadgeSource(),
    });
  }

  private updateIsVisible(): void {
    const entity = this.entity();
    if (!entity) {
      this.isVisible$.next(false);
      return;
    }

    const isOpen = this.isShopOpened(entity);
    const visible = this.displayOpenedShop() === isOpen && this.displayClosedShop() !== isOpen;
    this.isVisible$.next(visible);
  }

  private loadEntity(entity?: DoodEntityModel, parameters?: SettingsParametersState): void {
    let wantedAt = null;
    if (parameters?.wanted_at) {
      wantedAt = parameters?.wanted_at;
    }
    if (!wantedAt) {
      wantedAt = new Date();
    }

    this.status = this.entityStatusService.calculateStatus(
      entity,
      parameters?.distribution_mode,
      wantedAt,
    );
    this.entity.set(entity);
    const images = entity?.images;
    if (this.entityImageType() === 'overview') {
      this.shopImage = images?.overview?.url ?? images?.cover?.url;
    } else {
      this.shopImage = images?.cover?.url;
    }
    if (!this.shopImage && entity?.cover) {
      this.shopImage = 'https://api.dood.com' + entity?.cover;
    }
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(ShopCardBlockComponent, { 'keyAliases': {} });
